import { FC, Fragment } from 'react';
import styles from './NetDebtPartialTable2.module.scss';
import { useAppSelector } from '@core/hooks/redux-hooks';
import { useParams } from 'react-router-dom';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { getNetDebtIndex } from '@core/store/project-slice-selectors';
import GridTextField from '@app/shared/components/grid-controls/grid-text-field/GridTextField';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { getRequiredValidator } from '@core/validations/hook-forms/validators';
import Button from '@app/shared/components/button/Button';
import PlusSvg from '@app/shared/icons/Plus';
import MinusRoundedSvg from '@app/shared/icons/MinusRounded';
import { v4 as uuidv4 } from 'uuid';
import { ForecastDto } from '@app/shared/models/contracts/project-dto';
import { EquityBridgeColumnRole } from '@app/shared/models/contracts/enums/shared-enums';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  numericBaseFieldFormattingProps,
  textFieldFormattingProps,
} from '@app/shared/components/form-controls/form-field/form-field-patterns';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import {
  getDealThesisInputCaseForecastDate,
  getDealThesisInputCaseValuationDate,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import { useLocale } from '@app/core/hooks/useLocale';
import { DealThesisSpacerRow2 } from '../DealThesisSpacerRow2';
import DealThesisEmptyHistoricDataColumns from '../../deal-thesis-utils/DealThesisEmptyHistoricDataColumns';
import useDealThesisData from '../../deal-thesis-utils/useDealThesisData';
import { DealThesisFormModel } from '../../DealThesisNavigation';

interface NetDebtPartialTableProps {
  onRowsChange: () => void;
}

const NetDebtPartialTable2: FC<NetDebtPartialTableProps> = ({ onRowsChange }): JSX.Element => {
  const { l } = useLocale();
  const { control, getValues } = useFormContext<DealThesisFormModel>();
  const { caseId } = useParams();
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedInputs);
  const project = useAppSelector((state) => state.project.projectDraft);
  const { equityBridge } = project;

  const {
    filteredHistoricEventData,
    historicDataForecastYearsResults,
    getMaxColspan,
    showHistoricDataColumns,
    pwermInputHistoricForecasts: programableForecastYears,
    pwermCaseData,
    pwermCaseDataForecasts,
  } = useDealThesisData(caseId);

  const { replace: replaceNetDebtField } = useFieldArray({
    name: 'forecasts',
    control: control,
  });

  const { append: appendNetDebtField, remove: removeDebtField } = useFieldArray({
    name: 'netDebtItems',
    control: control,
  });

  const addNetDebt = () => {
    const netDebtItemId = uuidv4();
    appendNetDebtField(
      { netDebtItemId, narrative: '', historicalValue: null },
      { shouldFocus: false }
    );

    replaceNetDebtField(
      getValues('forecasts').map((forecast) => ({
        ...forecast,
        netDebtValues: [...forecast.netDebtValues, { netDebtItemId, value: 0 }],
      }))
    );

    onRowsChange();
  };

  const removeNetDebtItem =
    (netDebtItemToDelete: { netDebtItemId: string; formIndex: number }) => (): void => {
      removeDebtField(netDebtItemToDelete.formIndex);
      replaceNetDebtField(
        getValues('forecasts').map((forecast) => ({
          ...forecast,
          netDebtValues: forecast.netDebtValues.filter(
            (netDebtValue) => netDebtValue.netDebtItemId !== netDebtItemToDelete.netDebtItemId
          ),
        }))
      );

      onRowsChange();
    };

  const showHistoricNetDebt = showHistoricDataColumns && programableForecastYears.length > 0;

  const valuationColumn = equityBridge.columns.find(
    (column) => column.role === EquityBridgeColumnRole.Valuation
  );

  const netDebtItemsWithValues = equityBridge.netDebtItems
    .map((netDebtItem, formIndex) => ({ netDebtItem: netDebtItem, formIndex }))
    .filter(
      ({ netDebtItem: netDebtItem }) =>
        equityBridge.commonNetDebtItemIds.includes(netDebtItem.netDebtItemId) ||
        pwermCaseData?.forecasts.some((forecast: ForecastDto) =>
          forecast.netDebtValues.some(
            (netDebtValue) =>
              netDebtValue.netDebtItemId === netDebtItem.netDebtItemId && netDebtValue.value != null
          )
        )
    )
    .map(({ netDebtItem, formIndex }) => ({
      netDebtItemId: netDebtItem.netDebtItemId,
      narrative: netDebtItem.narrative,
      historicalValue: valuationColumn?.netDebtValues[netDebtItem.netDebtItemId] ?? null,
      formIndex,
      isCommon: equityBridge.commonNetDebtItemIds.includes(netDebtItem.netDebtItemId),
    }))
    .sort((a, b) => Number(b.isCommon) - Number(a.isCommon));

  return (
    <>
      <tbody>
        {caseId && (
          <>
            <tr>
              <th className="table-primary__cell--header">{l('_NetDebt')}</th>
              <td colSpan={getMaxColspan() - 1} className="table-primary__cell--header" />
            </tr>
            <tr>
              <th className="table-primary__cell--vertical-separator table-primary__cell--section-end-separator">
                {l('_CashNegative')}
              </th>
              <DealThesisEmptyHistoricDataColumns isHistoricEventsSection={false} isEndSeparator />
              {showHistoricDataColumns &&
                historicDataForecastYearsResults.map((_, index) => (
                  <td key={index} className="table-primary__cell--section-end-separator" />
                ))}
              <td className="table-primary__cell--section-end-separator">
                <GridTextField
                  name="valuationDateCashValue"
                  {...numericBaseFieldFormattingProps}
                  placeholder="0"
                  alignment={Alignment.Right}
                />
              </td>
              {pwermCaseDataForecasts?.map((forecast, index) => (
                <td
                  key={forecast.forecastId + 'cash-value'}
                  className="table-primary__cell--section-end-separator">
                  <GridTextField
                    name={`forecasts[${index}].cashValue`}
                    {...numericBaseFieldFormattingProps}
                    placeholder="0"
                    rules={{
                      ...getRequiredValidator(),
                    }}
                    alignment={Alignment.Right}
                  />
                </td>
              ))}
              <td className="table-primary__cell--section-end-separator" />
            </tr>
            {showHistoricNetDebt && (
              <tr>
                <th className="table-primary__cell--vertical-separator">{l('_HistoricNetDebt')}</th>
                <DealThesisEmptyHistoricDataColumns isHistoricEventsSection={false} />
                {historicDataForecastYearsResults.map((_, index) => (
                  <td key={index}>
                    <GridTextField
                      name={`historicForecasts.${
                        programableForecastYears.length - index - 1
                      }.historicNetDebt`}
                      alignment={Alignment.Right}
                      {...numericBaseFieldFormattingProps}
                      placeholder="0"
                    />
                  </td>
                ))}
                <td colSpan={getMaxColspan() - filteredHistoricEventData.length - 1} />
              </tr>
            )}
            {netDebtItemsWithValues.map((netDebtItem) => (
              <tr key={netDebtItem.netDebtItemId + 'debt-item'}>
                <th className="table-primary__cell--vertical-separator">
                  <div className={netDebtItem.isCommon ? styles.commonNetDebtNarrativeField : ''}>
                    <GridTextField
                      name={`netDebtItems.${netDebtItem.formIndex}.narrative`}
                      rules={{ ...getRequiredValidator() }}
                      placeholder={l('_AddNarrative')}
                      {...textFieldFormattingProps}
                    />
                  </div>
                </th>
                <DealThesisEmptyHistoricDataColumns isHistoricEventsSection={false} />
                {showHistoricDataColumns &&
                  historicDataForecastYearsResults.map((_, index) => <td key={index} />)}
                <td>
                  <GridTextField
                    name={`netDebtItems.${netDebtItem.formIndex}.historicalValue`}
                    {...numericBaseFieldFormattingProps}
                    placeholder="0"
                    alignment={Alignment.Right}
                  />
                </td>
                {pwermCaseDataForecasts?.map((forecast, index) => (
                  <Fragment key={forecast.forecastId + 'cash-value-totals'}>
                    <td>
                      <GridTextField
                        name={`forecasts[${index}].netDebtValues[${getNetDebtIndex(
                          forecast,
                          netDebtItem
                        )}].value`}
                        {...numericBaseFieldFormattingProps}
                        placeholder="0"
                        alignment={Alignment.Right}
                      />
                    </td>
                  </Fragment>
                ))}
                <td className="table-primary__cell--actions">
                  <Button
                    appearance={ButtonAppearance.CLEAN}
                    size={ButtonSize.TABLE_ACTION}
                    title={l('_DeleteItem')}
                    onClick={removeNetDebtItem(netDebtItem)}>
                    <MinusRoundedSvg />
                  </Button>
                </td>
              </tr>
            ))}
            <tr>
              <th className="table-primary__cell--vertical-separator">
                <div className="hide-on-screenshot">
                  <Button
                    appearance={ButtonAppearance.DEFAULT_TERTIARY}
                    size={ButtonSize.TABLE_ACTION}
                    startIcon={<PlusSvg />}
                    onClick={() => addNetDebt()}>
                    {l('_AddItem')}
                  </Button>
                </div>
              </th>
              <td colSpan={getMaxColspan() - 1} />
            </tr>
            <tr>
              <th className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator">
                <strong>{l('_TotalNetDebt')}</strong>
              </th>
              <DealThesisEmptyHistoricDataColumns
                isHistoricEventsSection={false}
                isStartSeparator
              />
              {showHistoricDataColumns &&
                historicDataForecastYearsResults.map((_, index) => (
                  <td key={index} className="table-primary__cell--section-start-separator" />
                ))}
              <td className="table-primary__cell--section-start-separator">
                <CellValue
                  {...amountRules}
                  strong
                  value={getDealThesisInputCaseValuationDate(
                    results,
                    caseId!,
                    (caseItem) => caseItem.totalNetDebt
                  )}
                />
              </td>
              {pwermCaseDataForecasts?.map((forecast) => (
                <td
                  key={forecast.forecastId + 'total-debt'}
                  className="table-primary__cell--section-start-separator">
                  <CellValue
                    {...amountRules}
                    strong
                    value={getDealThesisInputCaseForecastDate(
                      results,
                      caseId!,
                      forecast.forecastYear,
                      (forecast) => forecast?.totalNetDebt
                    )}
                  />
                </td>
              ))}
              <td className="table-primary__cell--section-start-separator" />
            </tr>
            <DealThesisSpacerRow2 />
          </>
        )}
      </tbody>
    </>
  );
};

export default NetDebtPartialTable2;
